import React, { useState, useEffect, useRef } from 'react'
import { messagingService } from '../lib/messagingService'
import { supabase } from '../lib/supabase'
import { useEventYear } from './EventYearContext'
import { User } from './AuthContext'

interface Journalist {
  id: string
  name: string
  outlet: string
  email: string
}

interface CreateGroupThreadProps {
  currentUser: User
  onThreadCreated: (threadId: string) => void
  onCancel: () => void
}

export const CreateGroupThread: React.FC<CreateGroupThreadProps> = ({
  currentUser,
  onThreadCreated,
  onCancel
}) => {
  const { currentYear } = useEventYear()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Journalist[]>([])
  const [selectedJournalists, setSelectedJournalists] = useState<Journalist[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [isLoadingTier, setIsLoadingTier] = useState(false)
  
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Search journalists with debounce
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setIsSearching(true)
        const results = await messagingService.searchJournalists(searchQuery)
        // Filter out already selected journalists
        const filteredResults = results.filter(
          result => !selectedJournalists.some(selected => selected.id === result.id)
        )
        setSearchResults(filteredResults)
        setShowDropdown(true)
        setIsSearching(false)
      } else {
        setSearchResults([])
        setShowDropdown(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, selectedJournalists])

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleAddJournalist = (journalist: Journalist) => {
    setSelectedJournalists(prev => [...prev, journalist])
    setSearchQuery('')
    setSearchResults([])
    setShowDropdown(false)
    searchInputRef.current?.focus()
  }

  const handleRemoveJournalist = (journalistId: string) => {
    setSelectedJournalists(prev => prev.filter(j => j.id !== journalistId))
  }

  const handleAddByTier = async (tier: 'A' | 'B') => {
    setIsLoadingTier(true)
    try {
      // Get journalists with this tier (and above for B: A+B) from journalists table
      const tiers = tier === 'A' ? ['A'] : ['A', 'B']
      const { data: tierJournalists, error: tierError } = await supabase
        .from('journalists')
        .select('email, name, outlet')
        .in('press_tier', tiers)
        .eq('event_year', currentYear)

      if (tierError) throw tierError
      if (!tierJournalists || tierJournalists.length === 0) {
        alert(`No ${tier === 'A' ? 'A-tier' : 'A or B-tier'} journalists found for ${currentYear}.`)
        return
      }

      // Match to user accounts by email
      const emails = tierJournalists.map(j => j.email)
      const { data: users, error: userError } = await supabase
        .from('users')
        .select('id, name, outlet, email')
        .eq('role', 'journalist')
        .in('email', emails)

      if (userError) throw userError

      if (!users || users.length === 0) {
        alert(`No ${tier === 'A' ? 'A-tier' : 'A or B-tier'} journalists have user accounts yet. They need to be invited first.`)
        return
      }

      // Add users that aren't already selected
      const existingIds = new Set(selectedJournalists.map(j => j.id))
      const newJournalists = users.filter(u => !existingIds.has(u.id))

      if (newJournalists.length === 0) {
        alert('All matching journalists are already added.')
        return
      }

      setSelectedJournalists(prev => [...prev, ...newJournalists])
    } catch (error) {
      console.error('Error loading journalists by tier:', error)
      alert('Failed to load journalists. Please try again.')
    } finally {
      setIsLoadingTier(false)
    }
  }

  const handleCreateThread = async () => {
    if (!title.trim() || selectedJournalists.length === 0) return
    
    setIsCreating(true)
    
    try {
      const thread = await messagingService.createGroupThread(
        title.trim(),
        description.trim(),
        currentUser.id,
        selectedJournalists.map(j => j.id)
      )
      
      if (thread) {
        onThreadCreated(thread.id)
      } else {
        alert('Failed to create group thread. Please try again.')
      }
    } catch (error) {
      console.error('Error creating thread:', error)
      alert('Failed to create group thread. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  const canCreate = title.trim() && selectedJournalists.length > 0 && !isCreating

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create Group Thread</h2>
          <p className="text-sm text-gray-600 mt-1">Start a conversation with specific journalists</p>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Thread Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Thread Name *
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Brad Pitt interviews"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this conversation..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
              maxLength={500}
            />
          </div>

          {/* Add Journalists */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Add Journalists *
            </label>

            {/* Quick-add by tier */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => handleAddByTier('A')}
                disabled={isLoadingTier}
                className="px-3 py-1.5 text-xs font-medium bg-rose-50 text-rose-700 border border-rose-300 rounded-md hover:bg-rose-100 disabled:opacity-50"
              >
                {isLoadingTier ? '...' : 'Add All A Press'}
              </button>
              <button
                type="button"
                onClick={() => handleAddByTier('B')}
                disabled={isLoadingTier}
                className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-300 rounded-md hover:bg-amber-100 disabled:opacity-50"
              >
                {isLoadingTier ? '...' : 'Add All A+B Press'}
              </button>
            </div>

            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, outlet, or email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
              
              {/* Search Dropdown */}
              {showDropdown && (
                <div 
                  ref={dropdownRef}
                  className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto z-10 mt-1"
                >
                  {isSearching ? (
                    <div className="p-3 text-center text-gray-500">Searching...</div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((journalist) => (
                      <button
                        key={journalist.id}
                        onClick={() => handleAddJournalist(journalist)}
                        className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{journalist.name}</div>
                        <div className="text-sm text-gray-600">{journalist.outlet}</div>
                      </button>
                    ))
                  ) : searchQuery.trim().length >= 2 ? (
                    <div className="p-3 text-center text-gray-500">No journalists found</div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Selected Journalists */}
          {selectedJournalists.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selected Journalists ({selectedJournalists.length})
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {selectedJournalists.map((journalist) => (
                  <div 
                    key={journalist.id}
                    className="flex items-center justify-between bg-gray-50 p-2 rounded-md"
                  >
                    <div>
                      <div className="font-medium text-sm">{journalist.name}</div>
                      <div className="text-xs text-gray-600">{journalist.outlet}</div>
                    </div>
                    <button
                      onClick={() => handleRemoveJournalist(journalist.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove journalist"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={isCreating}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateThread}
            disabled={!canCreate}
            className="px-4 py-2 bg-pink-600 text-white rounded-md font-medium hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Creating...' : 'Create Thread'}
          </button>
        </div>
      </div>
    </div>
  )
}